/**
 * React Hook: Request SOQL correction from the agent (self-correct flow)
 *
 * Uses POST /v2/agent/chat with a message that includes the failed query and
 * Salesforce error. Use when /soql/execute returns an error and you want
 * the agent to suggest a corrected query.
 *
 * @example
 * ```tsx
 * const { requestCorrection } = useRequestSoqlCorrection();
 * const result = await requestCorrection({
 *   originalMessage: query,
 *   failedQuery: currentQuery,
 *   sfError: data.error,
 * });
 * // result?.query, result?.explanation
 * ```
 */

import { useMutation } from '@tanstack/react-query';
import { useAuthStoreV1 } from '@/stores/authStore_v1';
import { getOrgAbortSignal, isAbortError } from '@/lib/org-scope';
import { agentChat } from '@/services/text-to-soql';

export interface RequestSoqlCorrectionArgs {
  originalMessage: string;
  failedQuery: string;
  sfError: string;
}

export interface SoqlCorrectionResult {
  query: string;
  explanation: string;
}

const SESSION_ID = 'text-to-soql';

function buildCorrectionMessage(args: RequestSoqlCorrectionArgs): string {
  return `The following SOQL query failed with a Salesforce error. Please fix the query and return ONLY the corrected SOQL.

Original request: ${args.originalMessage}

Failed query:
${args.failedQuery}

Salesforce error:
${args.sfError}

Please generate a corrected SOQL query that avoids this error.`;
}

export function useRequestSoqlCorrection() {
  const selectedOrg = useAuthStoreV1((state) => state.selectedOrg);
  const userId = useAuthStoreV1((state) => state.user?.id);

  const mutation = useMutation<
    SoqlCorrectionResult | null,
    Error,
    RequestSoqlCorrectionArgs
  >({
    mutationFn: async (args: RequestSoqlCorrectionArgs) => {
      if (!selectedOrg) {
        throw new Error('No organization selected');
      }
      const message = buildCorrectionMessage(args);
      const data = await agentChat(
        { message, session_id: SESSION_ID },
        selectedOrg,
        userId,
        getOrgAbortSignal(),
      );
      if (data.query && data.query !== args.failedQuery) {
        return {
          query: data.query,
          explanation: data.explanation ?? '',
        };
      }
      return null;
    },
    onError: (error) => {
      if (isAbortError(error)) return; // the org changed; not a failure
      console.error('❌ Failed to request SOQL correction:', error);
    },
  });

  return {
    requestCorrection: mutation.mutateAsync,
    isPending: mutation.isPending,
    data: mutation.data ?? null,
    error: mutation.error,
    isSuccess: mutation.isSuccess,
    reset: mutation.reset,
  };
}
